import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "card" }
const _hoisted_2 = { class: "field" }
const _hoisted_3 = { class: "field" }
const _hoisted_4 = { class: "flex justify-content-end" }

import { ref, onBeforeMount, toRefs } from 'vue';
import Dialog from 'primevue/dialog';
import { required } from '@vuelidate/validators';
import { useRouting, useValidation } from '@/composables';
import { EMIT } from '@/enum';
import { IntegrationSchoolDayService } from '@/services';
import { useTenantStore, useTenantIntegrationStore } from '@/store';
import { OrganizationInterface } from '@/types';

interface PropsInterface {
  connection?: null;
  integration?: null;
  tenantId: number | null;
}

interface PayloadInterface {
  baseOrganization: null | number;
  sisOrganization: null | {
    id: string;
    nces_id: string;
    school_number: string;
    name: string;
    type: string;
    status: string;
    modified_at: string;
  };
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SchoolDaySISOrganizationModal',
  props: {
    connection: { default: null },
    integration: { default: null },
    tenantId: { default: null }
  },
  emits: [EMIT.CANCEL, EMIT.UPDATE],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const tenantStore = useTenantStore();

const tenantIntegrationStore = useTenantIntegrationStore();

const $routing = useRouting();

const $validation = useValidation();

const { connection, integration, tenantId } = toRefs(props);

const emit = __emit;

const baseOrganizations: OrganizationInterface[] = ref([]);

const sisOrganizations = ref([]);

const modalVisible = ref(true);

const payload: PayloadInterface = ref({
  baseOrganization: null,
  sisOrganization: null,
});

const rules = {
  baseOrganization: { required },
  sisOrganization: { required },
};

const $v = $validation.defineValidation(rules, payload);

const submitted = ref(false);

onBeforeMount(async () => init());

const init = async () => {
  await defineOrganizations();
  await definePayload();
};

const defineOrganizations = async () => {
  const response = await IntegrationSchoolDayService.organizations(Number($routing.route.params.id));
  const integrationOrganizations = integration.value.integration_organizations;
  const isUpdateModal = tenantIntegrationStore.modalAction === 'update';

  baseOrganizations.value = tenantStore.tenant?.organizations.filter((item) => {
    if (isUpdateModal && connection.value && connection.value.organization_id === item.id) return item;
    if (!integrationOrganizations.find((aItem) => aItem.organization_id === item.id)) return item;
  });

  sisOrganizations.value = response.data.data.filter((item) => {
    if (isUpdateModal && connection.value && connection.value.organization_sis_id === item.id) return item;
    if (!integrationOrganizations.find((aItem) => aItem.organization_sis_id === item.id)) return item;
  });
};

const definePayload = async () => {
  if (connection.value) {
    payload.value = {
      baseOrganization: connection.value.organization_id,
      sisOrganization: sisOrganizations.value.find((item) => item.id === connection.value.organization_sis_id),
    };
  }
};

const onCancel = () => {
  modalVisible.value = false;
  $validation.resetDirty();
  emit(EMIT.CANCEL);
};

const onSave = async () => {
  submitted.value = true;
  const payloadData = {
    organization_id: payload.value.baseOrganization,
    organization_sis: {
      sis_id: payload.value.sisOrganization.id,
      nces_id: payload.value.sisOrganization.nces_id,
      school_number: payload.value.sisOrganization.school_number,
      name: payload.value.sisOrganization.name,
      type: payload.value.sisOrganization.type,
      status: payload.value.sisOrganization.status,
      modified_at: payload.value.sisOrganization.modified_at,
    },
  };

  connection.value
    ? await tenantIntegrationStore.updateTenantIntegrationOrganization(
        tenantId.value,
        integration.value.id,
        connection.value.id,
        payloadData,
      )
    : await tenantIntegrationStore.saveTenantIntegrationOrganization(tenantId.value, integration.value.id, payloadData);

  tenantIntegrationStore.openModal = false;
  tenantIntegrationStore.modalAction = 'create';
  onCancel();
};

return (_ctx: any,_cache: any) => {
  const _component_Dropdown = _resolveComponent("Dropdown")!
  const _component_AppButton = _resolveComponent("AppButton")!

  return (_openBlock(), _createBlock(_unref(Dialog), {
    visible: modalVisible.value,
    "onUpdate:visible": _cache[2] || (_cache[2] = ($event: any) => ((modalVisible).value = $event)),
    modal: true,
    header: "Connect Organizations",
    style: { width: '600px' },
    draggable: false,
    onHide: onCancel
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_component_AppButton, {
          type: "secondary",
          text: "Cancel",
          class: "mr-2",
          disabled: submitted.value,
          onClick: onCancel
        }, null, 8, ["disabled"]),
        _createVNode(_component_AppButton, {
          type: "primary",
          text: "Connect Organizations",
          disabled: _unref($v).$invalid || submitted.value,
          onClick: onSave
        }, null, 8, ["disabled"])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[3] || (_cache[3] = _createElementVNode("label", {
            class: "required",
            for: "baseOrganization"
          }, "Organization", -1)),
          _createVNode(_component_Dropdown, {
            id: "baseOrganization",
            modelValue: _unref($v).baseOrganization.$model,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref($v).baseOrganization.$model) = $event)),
            options: baseOrganizations.value,
            "option-label": "name",
            "option-value": "id",
            "empty-message": "There are no organizations available to be selected",
            filter: true,
            "virtual-scroller-options": { itemSize: 35 },
            class: _normalizeClass(_unref($validation).fieldStyles('baseOrganization', 'w-full')),
            placeholder: "Select an Organization",
            disabled: submitted.value
          }, null, 8, ["modelValue", "options", "class", "disabled"])
        ])
      ]),
      _cache[5] || (_cache[5] = _createElementVNode("div", { class: "field" }, [
        _createElementVNode("div", { class: "flex p-2" }, [
          _createElementVNode("div", { class: "flex w-4 mt-2 border-top-1 border-primary-500" }),
          _createElementVNode("div", { class: "flex-grow-1 flex align-items-center justify-content-center" }, [
            _createElementVNode("span", { class: "font-semibold text-600" }, "Will Be Connected To")
          ]),
          _createElementVNode("div", { class: "flex w-4 mt-2 border-top-1 border-primary-500" })
        ])
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _cache[4] || (_cache[4] = _createElementVNode("label", {
          class: "required",
          for: "sisOrganization"
        }, "SIS Organization", -1)),
        _createVNode(_component_Dropdown, {
          id: "baseOrganization",
          modelValue: _unref($v).sisOrganization.$model,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref($v).sisOrganization.$model) = $event)),
          options: sisOrganizations.value,
          "option-label": "name",
          "empty-message": "Organizations not available, verify the authentication credentials and try again",
          filter: true,
          "virtual-scroller-options": { itemSize: 35 },
          class: _normalizeClass(_unref($validation).fieldStyles('sisOrganization', 'w-full')),
          placeholder: "Select an SIS Organization",
          disabled: submitted.value
        }, null, 8, ["modelValue", "options", "class", "disabled"])
      ])
    ]),
    _: 1
  }, 8, ["visible"]))
}
}

})